/**
 * The bitmap fonts the nameplates, the level plates and the chat bubbles are
 * drawn with.
 *
 * The original drew them as Pixi Text: a canvas rasterised and uploaded as a
 * texture whenever the text changed, which for a nameplate is every rank
 * change, every teammate health tick and every zoom step, with a 10 px
 * blurred glow that is the slow part of a canvas raster. A BitmapText lays
 * its glyphs out as quads from an atlas rendered once per font, so a change
 * costs a layout and a zoom costs nothing. The atlases are rendered with the
 * style the Text had, glow included, in white: Pixi tints the quads with the
 * text's fill, so one atlas serves every team colour. The ASCII set is
 * rendered up front; any other character is added to the atlas the first
 * time a name uses it.
 *
 * setup waits for the web font before rendering anything: a glyph rendered
 * with the fallback face would keep it for good. A web font that can't be
 * fetched (blocked, or the network failing) leaves the fallback faces to draw
 * with, as they would for the page's own text, rather than failing the start.
 */
import type { TextDropShadow } from 'pixi.js';
import { BitmapFont, BitmapFontManager } from 'pixi.js';

/** The document's web font, which main.css declares. */
const WEB_FONT = 'MontserratWeb';
const FONT_FAMILY = `${WEB_FONT}, Helvetica, sans-serif`;

/** The nameplate font, with its glow; the size the Text had, drawn at half scale. */
export const NAMEPLATE_FONT = 'nameplate';
export const NAMEPLATE_FONT_SIZE = 33;
/** The level plate font, with a tighter glow. */
export const LEVEL_FONT = 'nameplate-level';
export const LEVEL_FONT_SIZE = 28;
/** The chat bubble font, plain. */
export const BUBBLE_FONT = 'bubble';
export const BUBBLE_FONT_SIZE = 12;

/** The black glow under the text: a drop shadow with no offset. */
function glow(blur: number): TextDropShadow {
  return { alpha: 1, angle: Math.PI / 4, blur, color: 0x000000, distance: 0 };
}

/** Installs the three fonts once the web font has loaded, at the canvas's resolution so they stay sharp with HiDPI on. */
export async function setup(resolution: number): Promise<void> {
  await document.fonts.load(`${NAMEPLATE_FONT_SIZE}px '${WEB_FONT}'`).catch((error: unknown) => {
    console.warn(`${WEB_FONT} could not be loaded, so the nameplates use the fallback font:`, error);
  });
  // The padding is the room each glyph's glow gets in the atlas: past about
  // one and a half times the blur the glow is under a level in 255.
  BitmapFont.install({
    name: NAMEPLATE_FONT,
    style: { fontFamily: FONT_FAMILY, fontSize: NAMEPLATE_FONT_SIZE, fill: 0xffffff, dropShadow: glow(10) },
    padding: 14,
    resolution,
    chars: BitmapFontManager.ASCII,
  });
  BitmapFont.install({
    name: LEVEL_FONT,
    style: { fontFamily: FONT_FAMILY, fontSize: LEVEL_FONT_SIZE, fill: 0xffffff, dropShadow: glow(6) },
    padding: 10,
    resolution,
    chars: BitmapFontManager.ASCII,
  });
  BitmapFont.install({
    name: BUBBLE_FONT,
    style: { fontFamily: FONT_FAMILY, fontSize: BUBBLE_FONT_SIZE, fill: 0xffffff },
    resolution,
    chars: BitmapFontManager.ASCII,
  });
}
